import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Lock, Search, TrendingUp, X } from 'lucide-react';
import { toast } from 'sonner';

import { attendanceApi, broadcastApi } from '../../api/examApi';
import { DashboardLayout } from '../../components/layout';
import { useAuth } from '../../context/AuthContext';
import { parseApiError } from '../../lib/apiErrors';

const todayIso = () => new Date().toISOString().slice(0, 10);

export default function AttendancePage() {
  const { isFaculty } = useAuth();
  const [date, setDate] = useState(todayIso());
  const [groups, setGroups] = useState([]);
  const [groupId, setGroupId] = useState('');
  const [nameFilter, setNameFilter] = useState('');
  const [students, setStudents] = useState([]);
  const [statusByStudent, setStatusByStudent] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Faculty can only mark/edit today's attendance; past dates are read-only
  // for them (server enforces this too — this is UX, not the real gate).
  // Admin has correction authority over any date.
  const isViewOnly = isFaculty() && date !== todayIso();

  useEffect(() => {
    broadcastApi.getEligibleGroups().then(({ data }) => setGroups(data)).catch(() => {
      // Non-fatal — the group selector just stays empty; the flat roster still works.
    });
  }, []);

  const loadData = useCallback(async (forDate, forGroupId) => {
    setLoading(true);
    try {
      const [studentsRes, attendanceRes] = await Promise.all([
        attendanceApi.getScopedStudents(forGroupId || undefined),
        attendanceApi.getByDate(forDate, forGroupId || undefined),
      ]);
      setStudents(studentsRes.data);
      const map = {};
      attendanceRes.data.forEach((record) => {
        map[record.student_id] = record.status;
      });
      setStatusByStudent(map);
    } catch (error) {
      toast.error(parseApiError(error, 'Failed to load attendance').message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData(date, groupId);
  }, [date, groupId, loadData]);

  const setStatus = (studentId, status) => {
    if (isViewOnly) return;
    setStatusByStudent((prev) => ({ ...prev, [studentId]: status }));
  };

  const markedCount = useMemo(
    () => students.filter((s) => statusByStudent[s.id]).length,
    [students, statusByStudent]
  );

  // Display-only filter — never mutates statusByStudent, so marks made
  // before/while/after filtering all persist regardless of what's visible.
  const visibleStudents = useMemo(() => {
    const needle = nameFilter.trim().toLowerCase();
    if (!needle) return students;
    return students.filter((s) => s.name.toLowerCase().includes(needle));
  }, [students, nameFilter]);

  const handleSave = async () => {
    if (isViewOnly) return;
    const records = students
      .filter((s) => statusByStudent[s.id])
      .map((s) => ({ student_id: s.id, status: statusByStudent[s.id] }));

    if (records.length === 0) {
      toast.error('Mark at least one student before saving');
      return;
    }

    setSaving(true);
    try {
      await attendanceApi.markBulk(date, records);
      toast.success(`Attendance saved for ${records.length} student(s)`);
      await loadData(date, groupId);
    } catch (error) {
      toast.error(parseApiError(error, 'Failed to save attendance').message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout title="Attendance">
      <div className="max-w-4xl animate-fade-in space-y-6">
        <div className="flex flex-col gap-4 rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-white">Mark Attendance</h2>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              {markedCount} of {students.length} students marked
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Date</label>
            <input
              type="date"
              value={date}
              max={todayIso()}
              onChange={(e) => setDate(e.target.value)}
              className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:ring-2 focus:ring-primary dark:border-slate-600 dark:bg-slate-900 dark:text-white"
              data-testid="attendance-date-picker"
            />
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Group</label>
            <select
              value={groupId}
              onChange={(e) => setGroupId(e.target.value)}
              className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:ring-2 focus:ring-primary dark:border-slate-600 dark:bg-slate-900 dark:text-white"
              data-testid="attendance-group-select"
            >
              <option value="">All my groups</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>
          </div>
        </div>

        {isViewOnly && (
          <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-900/40 dark:bg-amber-900/10 dark:text-amber-300">
            <Lock size={16} />
            View only — faculty can only mark or edit today's attendance. Ask an admin to correct a past date.
          </div>
        )}

        <div className="relative">
          <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={nameFilter}
            onChange={(e) => setNameFilter(e.target.value)}
            placeholder="Filter by student name…"
            className="w-full rounded-md border border-slate-300 bg-white py-2 pl-9 pr-3 text-sm text-slate-900 focus:ring-2 focus:ring-primary dark:border-slate-600 dark:bg-slate-900 dark:text-white"
            data-testid="attendance-name-filter"
          />
        </div>

        <div className="rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800">
          {loading ? (
            <div className="p-6 space-y-2 animate-pulse">
              <div className="h-10 rounded bg-slate-200 dark:bg-slate-700" />
              <div className="h-10 rounded bg-slate-200 dark:bg-slate-700" />
              <div className="h-10 rounded bg-slate-200 dark:bg-slate-700" />
            </div>
          ) : students.length === 0 ? (
            <p className="p-6 text-sm text-slate-500 dark:text-slate-400">
              No approved students found. If you're faculty, ask an admin to assign you to a group.
            </p>
          ) : visibleStudents.length === 0 ? (
            <p className="p-6 text-sm text-slate-500 dark:text-slate-400">
              No students match "{nameFilter}".
            </p>
          ) : (
            <div className="divide-y divide-slate-200 dark:divide-slate-700">
              {visibleStudents.map((student) => {
                const status = statusByStudent[student.id];
                return (
                  <div
                    key={student.id}
                    className="flex items-center justify-between px-5 py-3"
                    data-testid={`attendance-row-${student.id}`}
                  >
                    <div>
                      <p className="font-medium text-slate-900 dark:text-white">{student.name}</p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">{student.email}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setStatus(student.id, 'present')}
                        disabled={isViewOnly}
                        data-testid={`attendance-present-${student.id}`}
                        className={`inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                          status === 'present'
                            ? 'bg-emerald-500 text-white'
                            : 'bg-slate-100 text-slate-600 hover:bg-emerald-100 hover:text-emerald-700 dark:bg-slate-700 dark:text-slate-300'
                        }`}
                      >
                        <Check size={13} /> Present
                      </button>
                      <button
                        onClick={() => setStatus(student.id, 'absent')}
                        disabled={isViewOnly}
                        data-testid={`attendance-absent-${student.id}`}
                        className={`inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                          status === 'absent'
                            ? 'bg-rose-500 text-white'
                            : 'bg-slate-100 text-slate-600 hover:bg-rose-100 hover:text-rose-700 dark:bg-slate-700 dark:text-slate-300'
                        }`}
                      >
                        <X size={13} /> Absent
                      </button>
                      {isFaculty() && (
                        <Link
                          to={`/faculty/students/${student.id}/ledger`}
                          data-testid={`attendance-progress-${student.id}`}
                          className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:border-primary hover:text-primary dark:border-slate-600 dark:text-slate-300 dark:hover:border-accent-cool dark:hover:text-accent-cool"
                        >
                          <TrendingUp size={13} /> Progress
                        </Link>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving || loading || isViewOnly}
            data-testid="attendance-save"
            className="rounded-md bg-primary px-6 py-2.5 text-sm font-medium text-white hover:bg-primary-light disabled:opacity-50"
          >
            {saving ? 'Saving...' : isViewOnly ? 'View Only' : 'Save Attendance'}
          </button>
        </div>
      </div>
    </DashboardLayout>
  );
}
